import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { PaymentBreakdownEntry } from "@/lib/currency";

// ─── Types ──────────────────────────────────────────────────────────────────

/** 'paid' when a completed payment row exists; null otherwise (unpaid = no data). */
export type ParticipationPaymentStatus = "paid" | null;
export type PaymentMethod = "stripe" | "cash" | null;

// ─── Queries ────────────────────────────────────────────────────────────────

export interface MemberPaymentRow {
  participation_id: number;
  person_id: number;
  is_free: boolean;
  payment_status: ParticipationPaymentStatus;
  payment_method: PaymentMethod;
  payment_amount_cents: number | null;
  /** What the payer actually handed over, in minor units of payment_currency_original. */
  payment_amount_original: number | null;
  /** ISO currency the payer actually paid in (e.g. 'gbp', 'eur'). */
  payment_currency_original: string | null;
  /** original->GBP multiplier used to book the GBP leg; null for GBP. */
  payment_exchange_rate: number | null;
  /** Individual payments behind the total — a person may pay more than once. */
  payments: PaymentBreakdownEntry[] | null;
  /** Default currency of the event's location — the cash-entry dropdown default. */
  location_default_currency: string | null;
  /** Event ticket price; null/0 = free event (no payment expected). */
  price_cents: number | null;
}

/**
 * Per-member payment state for a set of participations (admin/host results
 * view). RLS scopes rows: admin sees all, a host only their own participations.
 */
export async function fetchPaymentsForParticipations(
  participationIds: number[],
): Promise<MemberPaymentRow[]> {
  if (!participationIds.length) return [];
  const { data, error } = await supabase
    .from("v_member_payment")
    .select(
      "participation_id, person_id, is_free, payment_status, payment_method, payment_amount_cents, payment_amount_original, payment_currency_original, payment_exchange_rate, location_default_currency, price_cents, payments",
    )
    .in("participation_id", participationIds);
  if (error) throw error;
  return (
    (data ?? []) as unknown as Array<{
      participation_id: number;
      person_id: number;
      is_free: boolean;
      payment_status: string | null;
      payment_method: string | null;
      payment_amount_cents: number | null;
      payment_amount_original: number | null;
      payment_currency_original: string | null;
      payment_exchange_rate: number | null;
      location_default_currency: string | null;
      price_cents: number | null;
      payments: PaymentBreakdownEntry[] | null;
    }>
  ).map((r) => ({
    participation_id: r.participation_id,
    person_id: r.person_id,
    is_free: r.is_free ?? false,
    payment_status: (r.payment_status as ParticipationPaymentStatus) ?? null,
    payment_method: (r.payment_method as PaymentMethod) ?? null,
    payment_amount_cents: r.payment_amount_cents ?? null,
    payment_amount_original: r.payment_amount_original ?? null,
    payment_currency_original: r.payment_currency_original ?? null,
    payment_exchange_rate: r.payment_exchange_rate ?? null,
    location_default_currency: r.location_default_currency ?? null,
    price_cents: r.price_cents ?? null,
    payments: r.payments ?? null,
  }));
}

// ─── Aggregation helpers ────────────────────────────────────────────────────

export type TeamPaymentStatus = "free" | "paid" | "partial" | "unpaid" | null;

/**
 * Aggregate per-member payment state into a single team-level status.
 * Pure function. Uses is_free + payment_status (paid or null).
 */
export function teamPaymentStatus(
  members: ReadonlyArray<{
    payment_status: ParticipationPaymentStatus;
    is_free?: boolean;
  }>,
): TeamPaymentStatus {
  if (members.length === 0) return null;

  let hasPaid = false;
  let hasUnpaid = false;
  let hasFree = false;

  // is_free and payment_status are independent axes (#189): a member can be
  // both comped AND have paid (donation). Count paid on its own merits, and
  // only treat a member as owing when they're neither paid nor free.
  for (const m of members) {
    const paid = m.payment_status === "paid";
    if (m.is_free) hasFree = true;
    if (paid) hasPaid = true;
    if (!paid && !m.is_free) hasUnpaid = true;
  }

  if (hasPaid && hasUnpaid) return "partial";
  if (!hasPaid && !hasUnpaid && hasFree) return "free";
  if (hasPaid && !hasUnpaid) return "paid";
  return "unpaid";
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/**
 * Admin/host updates a single member's paid/unpaid state (per-person). Routes
 * through set_member_payment RPC which manages the payment row only — is_free
 * is independent (use setMemberFree).
 */
export interface UpdateMemberPaymentArgs {
  participationId: number;
  personId: number;
  status: "paid" | "unpaid";
  method?: PaymentMethod;
  /** Normalised GBP minor units (the settlement leg booked on the row). */
  amountCents?: number | null;
  /** What the payer actually handed over, in minor units of currencyOriginal. */
  amountOriginal?: number | null;
  /** ISO currency of amountOriginal (e.g. 'eur'); defaults to gbp server-side. */
  currencyOriginal?: string | null;
  /** original→GBP multiplier used to book amountCents; null for GBP. */
  exchangeRate?: number | null;
}

export async function updateMemberPayment(
  args: UpdateMemberPaymentArgs,
): Promise<void> {
  const { error } = await supabase.rpc("set_member_payment", {
    p_participation_id: args.participationId,
    p_person_id: args.personId,
    p_status: args.status,
    p_method: args.method ?? null,
    p_amount_cents: args.amountCents ?? null,
    p_amount_original: args.amountOriginal ?? null,
    p_currency_original: args.currencyOriginal ?? null,
    p_exchange_rate: args.exchangeRate ?? null,
  });
  if (error) throw error;
}

/** Toggle the is_free flag independently of paid/unpaid payment state. */
export async function setMemberFree(args: {
  participationId: number;
  personId: number;
  isFree: boolean;
}): Promise<void> {
  const { error } = await supabase.rpc("set_member_free", {
    p_participation_id: args.participationId,
    p_person_id: args.personId,
    p_is_free: args.isFree,
  });
  if (error) throw error;
}

// ─── Per-transaction writes ─────────────────────────────────────────────────
//
// A member may hold several payments for one event (card ticket + cash top-up,
// or a tip on top of a comped entry), so the Results editor works on individual
// rows rather than the single upserted row set_member_payment manages. Only
// manual rows are writable — the RPCs reject anything Stripe recorded.

/** The money legs of one payment, shared by add and update. */
export interface PaymentAmountArgs {
  method: "stripe" | "cash";
  /** Normalised GBP minor units (the settlement leg). */
  amountCents: number;
  /** What was handed over, in minor units of currencyOriginal. */
  amountOriginal: number | null;
  currencyOriginal: string;
  /** original→GBP multiplier; null for GBP. */
  exchangeRate: number | null;
}

export async function addMemberPayment(
  args: PaymentAmountArgs & { participationId: number; personId: number },
): Promise<void> {
  const { error } = await supabase.rpc("add_member_payment", {
    p_participation_id: args.participationId,
    p_person_id: args.personId,
    p_method: args.method,
    p_amount_cents: args.amountCents,
    p_amount_original: args.amountOriginal,
    p_currency_original: args.currencyOriginal,
    p_exchange_rate: args.exchangeRate,
  });
  if (error) throw error;
}

export async function updateMemberPaymentRow(
  args: PaymentAmountArgs & { paymentId: number },
): Promise<void> {
  const { error } = await supabase.rpc("update_member_payment_row", {
    p_payment_id: args.paymentId,
    p_method: args.method,
    p_amount_cents: args.amountCents,
    p_amount_original: args.amountOriginal,
    p_currency_original: args.currencyOriginal,
    p_exchange_rate: args.exchangeRate,
  });
  if (error) throw error;
}

export async function deleteMemberPaymentRow(paymentId: number): Promise<void> {
  const { error } = await supabase.rpc("delete_member_payment_row", {
    p_payment_id: paymentId,
  });
  if (error) throw error;
}

// ─── Revenue split ─────────────────────────────────────────────────────

export interface RevenueSplitEntry {
  amountCents: number;
  /** "stripe" money lands with us; cash/other stays with whoever collected it (the host). */
  method: string | null;
  /** Our share of this payment, 0..100. */
  ourSharePct: number;
}

export interface RevenueSplit {
  collectedCents: number;
  oursCents: number;
  hostCents: number;
  /** Net settlement: > 0 we owe the host, < 0 the host owes us. */
  netToHostCents: number;
}

/**
 * Split collected money between us and the host. Card payments arrive in our
 * Stripe account, so the host's share of those is owed to them; cash is held
 * by the host, so our share of that is owed to us.
 */
export function splitRevenue(
  entries: ReadonlyArray<RevenueSplitEntry>,
): RevenueSplit {
  let collected = 0;
  let ours = 0;
  let netToHost = 0;
  for (const e of entries) {
    const ourPart = (e.amountCents * e.ourSharePct) / 100;
    collected += e.amountCents;
    ours += ourPart;
    // No host share → nothing to settle (e.g. cash at an admin-run location).
    if (e.ourSharePct >= 100) continue;
    netToHost += e.method === "stripe" ? e.amountCents - ourPart : -ourPart;
  }
  const oursCents = Math.round(ours);
  return {
    collectedCents: collected,
    oursCents,
    hostCents: collected - oursCents,
    netToHostCents: Math.round(netToHost),
  };
}

/** Our share (0..100) for an event; null when the caller can't see it. */
export async function fetchEventOurSharePct(
  eventId: number,
): Promise<number | null> {
  const { data, error } = await supabase.rpc("event_our_share_pct", {
    p_event_id: eventId,
  });
  if (error) throw error;
  return data ?? null;
}

export interface FinanceTerms {
  /** location_id → our share and the date it starts (null = always). */
  locations: Map<number, { ourSharePct: number; shareFrom: string | null }>;
  /** event_id → per-event override of our share. */
  events: Map<number, number>;
}

/** Revenue-split terms visible to the caller (admin: all; host: their own). */
export async function fetchFinanceTerms(): Promise<FinanceTerms> {
  const [loc, ev] = await Promise.all([
    supabase
      .from("location_finance")
      .select("location_id, our_share_pct, share_from"),
    supabase.from("event_finance").select("event_id, our_share_pct"),
  ]);
  if (loc.error) throw loc.error;
  if (ev.error) throw ev.error;
  return {
    locations: new Map(
      (loc.data ?? []).map((r) => [
        r.location_id,
        { ourSharePct: r.our_share_pct, shareFrom: r.share_from },
      ]),
    ),
    events: new Map((ev.data ?? []).map((r) => [r.event_id, r.our_share_pct])),
  };
}

/** Admin only. ourSharePct null removes the terms (100% ours). */
export async function setLocationFinance(args: {
  locationId: number;
  ourSharePct: number | null;
  shareFrom: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("set_location_finance", {
    p_location_id: args.locationId,
    p_our_share_pct: args.ourSharePct,
    p_share_from: args.shareFrom,
  });
  if (error) throw error;
}

/** Admin only. ourSharePct null clears the override (location terms apply). */
export async function setEventFinance(args: {
  eventId: number;
  ourSharePct: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc("set_event_finance", {
    p_event_id: args.eventId,
    p_our_share_pct: args.ourSharePct,
  });
  if (error) throw error;
}

// ─── Host transfers ────────────────────────────────────────────────────

/**
 * A settlement between us and a location's host. It reduces the report
 * balance and is never revenue. to_us = host paid us; to_host = we paid them.
 * free_place = a seat given instead of money.
 */
export interface HostTransfer {
  id: number;
  locationId: number;
  city: string | null;
  paidOn: string;
  amountCents: number;
  direction: "to_us" | "to_host";
  via: "money" | "free_place";
  paymentId: number | null;
  note: string | null;
}

/** Transfers visible to the caller (admin: all; host: their locations). */
export async function fetchHostTransfers(): Promise<HostTransfer[]> {
  const { data, error } = await supabase
    .from("host_transfer")
    .select(
      "id, location_id, paid_on, amount_cents, direction, via, payment_id, note, location(city)",
    )
    .order("paid_on", { ascending: false })
    .order("id", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    locationId: r.location_id,
    city: r.location?.city ?? null,
    paidOn: r.paid_on,
    amountCents: r.amount_cents,
    direction: r.direction as HostTransfer["direction"],
    via: r.via as HostTransfer["via"],
    paymentId: r.payment_id,
    note: r.note,
  }));
}

/** Admin only. With paymentId, that donation becomes the transfer (to us, its amount). */
export async function addHostTransfer(args: {
  locationId: number;
  paidOn: string;
  amountCents: number;
  direction: HostTransfer["direction"];
  via?: HostTransfer["via"];
  note?: string | null;
  paymentId?: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc("add_host_transfer", {
    p_location_id: args.locationId,
    p_paid_on: args.paidOn,
    p_amount_cents: args.amountCents,
    p_direction: args.direction,
    p_via: args.via ?? "money",
    p_note: args.note ?? undefined,
    p_payment_id: args.paymentId ?? undefined,
  });
  if (error) throw error;
}

/** Admin only. A linked payment becomes a donation again. */
export async function deleteHostTransfer(id: number): Promise<void> {
  const { error } = await supabase.rpc("delete_host_transfer", { p_id: id });
  if (error) throw error;
}

// ─── Payments page ─────────────────────────────────────────────────────

/**
 * host_transfer – a donation that was really a host paying their share (not revenue)
 * donation   – no event at all (support payment)
 * unassigned – event set, but the person isn't registered for it
 * upcoming   – registered for an event that hasn't finished yet
 * no_show    – registered, event finished, no result recorded
 * attached   – registered and the participation has a result
 */
export type PaymentKind =
  | "host_transfer"
  | "donation"
  | "unassigned"
  | "upcoming"
  | "no_show"
  | "attached";

export type PaymentListRow =
  Database["public"]["Functions"]["list_payments"]["Returns"][number] & {
    kind: PaymentKind;
  };

/** All payments the caller may see: admins everything, hosts their locations. */
export async function fetchAllPayments(): Promise<PaymentListRow[]> {
  const { data, error } = await supabase.rpc("list_payments");
  if (error) throw error;
  return (data ?? []) as PaymentListRow[];
}

export type PaymentTarget =
  Database["public"]["Functions"]["list_payment_targets"]["Returns"][number];

/** Members of every participation of an event — candidates for a payment. */
export async function fetchPaymentTargets(
  eventId: number,
): Promise<PaymentTarget[]> {
  const { data, error } = await supabase.rpc("list_payment_targets", {
    p_event_id: eventId,
  });
  if (error) throw error;
  return data ?? [];
}

export type PaymentTargetMatch =
  Database["public"]["Functions"]["search_payment_targets"]["Returns"][number];

/**
 * Registrations (any event the caller manages) whose name or team contains
 * every word of the query. Newest event first, max 50; needs 2+ characters.
 */
export async function searchPaymentTargets(
  query: string,
): Promise<PaymentTargetMatch[]> {
  const { data, error } = await supabase.rpc("search_payment_targets", {
    p_query: query,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Move a payment onto a participation member, or (participationId = null,
 * admin only) detach it into a donation.
 */
export async function assignPayment(args: {
  paymentId: number;
  participationId: number | null;
  personId?: number;
}): Promise<void> {
  const { error } = await supabase.rpc("assign_payment", {
    p_payment_id: args.paymentId,
    p_participation_id: args.participationId,
    ...(args.personId != null ? { p_person_id: args.personId } : {}),
  });
  if (error) throw error;
}
